import React, { useEffect, useReducer, useState } from "react";
import { DcaContainer } from "./styled-components/DcaContainer";
import { Button, LinearProgress, Stack, Typography } from "@mui/material";
import {
  Chip,
  CryptoSelection,
  FrequencySelectorField,
  SpendLimitField,
  StyledLabel,
} from "./styled-components/DcaFields";
import StyledButton from "../dashboard/StyledButton";
import ClearIcon from "./icons/ClearIcon";
import BasicDatePicker from "./DatePicker";
import {
  resetDcaState,
  setErrorMessage,
  setModalState,
  setSellCurrency,
  setSpendLimitError,
  setSummaryResponse,
  useDcaSlice,
} from "src/store/features/dca.slice";
import api from "src/store/features/api";
import StrategyPlacedSuccess from "./StrategyPlacedSuccess";
import {
  GetDcaStrategySummaryApiResponse,
  DcaStrategyReqDto,
} from "src/store/features/api/generated";
import { useAppDispatch } from "src/store/store";
import OrderSummary from "./OrderSummary";
import { DcaSwitch } from "./icons/IOSSwitch";
import { Info } from "@mui/icons-material";
import SellAmountInputComponent from "./styled-components/DcaFields";
import { useUserSlice } from "src/store/features/user.slice";
import { ErrorComponent } from "./styled-components/ErrorText";
import { setSelectedToken } from "src/store/features/TargetOrders.slice";
import BalanceTooltip from "../wallets/header/BalanceTooltip";
import { trackEvent } from "src/Amplitude";
import usePermission from "src/utils/hooks/usePermission";
import useVerificationNavigation from "../common/useVerificationNavigation";

export default function CreateStrategyComponent() {
  const [getStrategySummary, { isLoading }] = api.useGetDcaStrategySummaryMutation();
  const [toggleState, invert] = useReducer((state) => !state, false);
  const dispatch = useAppDispatch();
  const { isGuest } = usePermission();
  const { handleVerification } = useVerificationNavigation();

  const {
    defaultCurrency: { code },
  } = useUserSlice();

  const {
    frequency,
    buyCurrency,
    sellCurrency,
    amount,
    startDate,
    endDate,
    spendLimit,
    modalState,
    errorMessage,
  } = useDcaSlice();

  useEffect(() => {
    // Remove any selected optional field when the toggle is off
    if (!toggleState) setOptionalFieldSelection(null);
  }, [toggleState]);

  const [optionalFieldSelection, setOptionalFieldSelection] = useState<
    "End date" | "Spend limit" | null
  >();

  const getSummary = async () => {
    // No amount entered
    if (!amount || amount === 0) return dispatch(setErrorMessage("Please enter an amount"));

    // No min amount met
    if (amount && amount < 50) return dispatch(setErrorMessage("Minimum trade size is A$50 "));

    // Add Limit toggled, but no optional field selected
    if (toggleState && !optionalFieldSelection)
      return dispatch(setErrorMessage("Please select a limit or toggle off to proceed"));

    // No spend limit entered
    if (optionalFieldSelection === "Spend limit" && !spendLimit)
      return dispatch(setSpendLimitError("Please Enter Spend Limit"));

    // Spend Limit is lower than amount selected
    if (optionalFieldSelection === "Spend limit" && spendLimit! < amount)
      return dispatch(setSpendLimitError("Spend Limit should be higher than amount"));

    if (amount) {
      const dcaStrategyReqDto: DcaStrategyReqDto = {
        amount,
        frequency,
        startDate,
        buy: {
          code: buyCurrency!,
        },
        sell: {
          code: sellCurrency ?? code,
          units: amount,
        },
        ...(optionalFieldSelection === "End date" ? { endDate } : {}),
        ...(optionalFieldSelection === "Spend limit" ? { spendLimit } : {}),
      };

      await getStrategySummary({ dcaStrategyReqDto })
        .unwrap()
        .then((response: GetDcaStrategySummaryApiResponse) => {
          trackEvent("User requested summary", { ...dcaStrategyReqDto }, "DCA");
          dispatch(setSummaryResponse(response));
          dispatch(setModalState("summary"));
        })
        .catch(({ message }) => {
          dispatch(setErrorMessage(message));
        });
    }
  };

  const clearCreateComponent = () => {
    dispatch(resetDcaState());
    dispatch(setSellCurrency(code));
    dispatch(setSelectedToken({ code: "BTC" }));
    dispatch(setModalState("create plan"));
    if (toggleState) invert();
    trackEvent("User cleared component", {}, "DCA");
  };

  const handleStrategyButtonClick = () => {
    handleVerification(getSummary);
  };

  return (
    <DcaContainer>
      {modalState === "create plan" && (
        <>
          <Stack direction={"row"} alignItems="center" justifyContent={"space-between"}>
            <StyledLabel title="Buy" />
            <Stack direction={"row"} alignItems="center">
              <Button
                disableRipple
                size="small"
                onClick={clearCreateComponent}
                sx={{
                  textTransform: "none",
                  padding: "0px",
                  fontSize: "14px",
                  borderRadius: "24px",
                }}>
                <Typography color="#5C6469" fontSize="14px">
                  Clear
                </Typography>{" "}
                <ClearIcon />
              </Button>
            </Stack>
          </Stack>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "left", md: "center" }}
            spacing={{ xs: 0.8, md: 1 }}>
            {/* Buy Selector */}
            <SellAmountInputComponent error={!!errorMessage} />
            {/* Division */}
            <StyledLabel title="of" />
            {/* Sell side Selector */}
            <CryptoSelection />
          </Stack>

          {/* Holdings Errors placeholder */}
          {!!errorMessage && <ErrorComponent text={errorMessage} />}

          {/* Frequency Selection */}
          <StyledLabel title="Every" />
          <FrequencySelectorField />

          {/* Starting Date Selection */}
          <StyledLabel title="Starting" />
          <BasicDatePicker source="start date" />

          {/* Add Limit */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
            <Stack direction="row" spacing={1} mt={1.5}>
              <StyledLabel title="Add Limit" />
              <BalanceTooltip
                message={
                  "You can add an end date or spend limit to your plan. The plan will automatically stop once your selected spent limit or end date is reached. Click on the toggle to enter a limit."
                }
                contrast>
                <Info style={{ fontSize: "17px" }} />
              </BalanceTooltip>
            </Stack>

            <div onClick={invert} style={{ marginTop: "6px" }}>
              <DcaSwitch stage={toggleState ? "on" : "off"} />
            </div>
          </div>

          {toggleState && ( // if toggle is on display the optional fields
            <>
              {/* End date, Spend limit switch */}
              <Stack direction="row" spacing={1} mb={2}>
                {["End date", "Spend limit"].map((item, it) => (
                  <Chip
                    key={item + it}
                    selected={optionalFieldSelection === item}
                    onClick={() => {
                      dispatch(setErrorMessage(""));
                      setOptionalFieldSelection(item as any);
                    }}>
                    {item}
                  </Chip>
                ))}
              </Stack>

              {/* Add Limit error placeholder */}
              {!!errorMessage && errorMessage.includes("toggle off") && (
                <div style={{ marginTop: "-16px" }}>
                  <ErrorComponent text={errorMessage} />
                </div>
              )}

              {/* Enter Spend Limit Field */}
              {optionalFieldSelection === "Spend limit" && <SpendLimitField />}

              {/* Enter End Date Field */}
              {optionalFieldSelection === "End date" && <BasicDatePicker source={"end date"} />}
            </>
          )}

          {/* Create Strategy Button */}
          <StyledButton
            sx={{ mt: "30px" }}
            onClick={handleStrategyButtonClick}
            disabled={isLoading}>
            {isGuest ? "Verify Your Account" : "Schedule order"}
          </StyledButton>
          {isLoading && (
            <LinearProgress
              sx={{
                marginTop: "14px",
                width: "95%",
                marginLeft: "8px",
              }}
            />
          )}
        </>
      )}
      {modalState === "summary" && <OrderSummary />}
      {modalState === "success" && (
        <Stack spacing={4}>
          <StrategyPlacedSuccess />
          <StyledButton onClick={clearCreateComponent}>Close</StyledButton>
        </Stack>
      )}
    </DcaContainer>
  );
}
